import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import projectService from '../../api/projectService';
import 'swiper/css';
import 'swiper/css/navigation';
import './ProjectSlider.css';
import { Link } from 'react-router-dom';

const ProjectSlider = () => {
  const [activeFilter, setActiveFilter] = useState('All Projects');
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]); 
  const [categories, setCategories] = useState(['All Projects']);
  
  // State to track which image is currently open in the popup modal
  const [selectedImage, setSelectedImage] = useState(null);

  // Helper for Image URLs to fix alignment/visibility issues
  const getImageUrl = (img) => {
  if (!img) return 'https://via.placeholder.com/400x500?text=No+Image';
  if (img.startsWith('http')) return img;

    // Use environment variable for flexibility
    const BASE_URL = process.env.REACT_APP_API_IMG_URL || "http://localhost:4000";

    return `${BASE_URL}/${img.replace(/^\//, '')}`;
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const allProjects = await projectService.getAllProjects();
      setProjects(allProjects);

      if (allProjects && allProjects.length > 0) {
        const uniqueCats = [
          'All Projects',
          ...new Set(allProjects.map(p => p.category).filter(Boolean))
        ];
        setCategories(uniqueCats);
      }
    } catch (error) {
      console.error("Slider Load Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Client-side filtering of the full list
  const filteredProjects = activeFilter === 'All Projects' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  return (
    <section className="project-slider-section">
      <div className="container text-center mb-5">
        <div className="d-flex justify-content-center flex-wrap gap-4 border-bottom pb-2">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-tab ${activeFilter === cat ? 'active' : ''}`}
              onClick={() => setActiveFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="slider-container px-3 position-relative" style={{ minHeight: '450px' }}>
        {isLoading ? (
          <div className="project-loader-overlay d-flex justify-content-center align-items-center">
            <div className="spinner-border text-warning" role="status"></div>
          </div>
        ) : (
          <Swiper
            key={filteredProjects.length} 
            modules={[Navigation, Autoplay]}
            spaceBetween={25}
            slidesPerView={1}
            loop={filteredProjects.length >= 4}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            navigation={{ nextEl: '.slide-next', prevEl: '.slide-prev' }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 4 },
            }}
          >
            {filteredProjects.map((project) => (
              <SwiperSlide key={project._id}>
                <div className="project-card-vps">
                  {/* Clicking the image container opens the popup */}
                  <div 
                    className="project-img-box" 
                    onClick={() => setSelectedImage(getImageUrl(project.img))}
                    style={{ cursor: 'zoom-in' }}
                  >
                    <img 
                      src={getImageUrl(project.img)} 
                      alt={project.title} 
                      style={{ width: '100%', height: '450px', objectFit: 'cover' }} 
                    />
                    <div className="project-info-overlay">
                      <p className="text-warning fw-bold small mb-1">{project.category}</p>
                      <h5 className="text-white fw-bold">{project.title}</h5>
                      <p className="text-light small mb-0">
                        <i className="bi bi-geo-alt me-1"></i>{project.location || 'Location N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      <div className="project-controls-footer mt-4">
        <div className="container d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-warning slide-prev px-3 py-2 rounded-0 shadow-sm"><i className="bi bi-arrow-left"></i></button>
            <button className="btn btn-warning slide-next px-3 py-2 rounded-0 shadow-sm"><i className="bi bi-arrow-right"></i></button>
          </div>
          <Link to="/contact" className="text-decoration-none">
            <button className="btn btn-warning fw-bold px-4 py-3 rounded-0 shadow-sm">
              Contact Us Now <i className="bi bi-arrow-up-right ms-2"></i>
            </button>
          </Link>
        </div>
      </div>

      {/* --- IMAGE MODAL POPUP BACKDROP --- */}
      {selectedImage && (
        <div 
          className="image-popup-overlay" 
          onClick={() => setSelectedImage(null)} // Close when clicking the backdrop background
        >
          <div className="popup-content-wrapper" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button 
              className="popup-close-btn" 
              onClick={() => setSelectedImage(null)}
              aria-label="Close popup"
            >
              <i className="bi bi-x-lg"></i>
            </button>
            
            {/* Expanded Image */}
            <img 
              src={selectedImage} 
              alt="Project Showcase Expanded View" 
              className="popup-img-element"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectSlider;